import { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import SectionHeader from "./SectionHeader";
import { captureError } from "src/utils/errorReporting";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import {
    uploadPumppuRequestAttachment,
    deleteAttachment,
} from "src/utils/quote/quoteAttachments";

function isPdf(att) {
    return (att.fileType || "").toLowerCase() === "application/pdf";
}

function Thumbnail({ attachment, onDelete, disabled }) {
    const theme = useTheme();
    const radius = theme.custom?.wizard?.microRadius ?? 14;
    const pdf = isPdf(attachment);
    const t = useT();
    return (
        <Box
            sx={{
                position: "relative",
                width: 96,
                height: 96,
                borderRadius: `${radius}px`,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "rgba(244,242,238,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {pdf ? (
                <Stack spacing={0.5} sx={{ alignItems: "center" }}>
                    <PictureAsPdfIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", px: 0.5, textAlign: "center" }} noWrap>
                        {attachment.origFileName || "PDF"}
                    </Typography>
                </Stack>
            ) : attachment.url ? (
                <img
                    src={attachment.url}
                    alt={attachment.origFileName || t("wizard.attachmentsSection.altFallback")}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    loading="lazy"
                />
            ) : (
                <CircularProgress size={20} />
            )}
            <IconButton
                size="small"
                aria-label={t("wizard.attachmentsSection.deleteAria")}
                disabled={disabled}
                onClick={() => onDelete(attachment)}
                sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "text.primary",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    width: 22,
                    height: 22,
                }}
            >
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </Box>
    );
}

function PendingThumbnail({ name }) {
    const theme = useTheme();
    const radius = theme.custom?.wizard?.microRadius ?? 14;
    return (
        <Box
            sx={{
                width: 96,
                height: 96,
                borderRadius: `${radius}px`,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                px: 0.5,
            }}
        >
            <CircularProgress size={18} />
            <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", textAlign: "center" }} noWrap>
                {name}
            </Typography>
        </Box>
    );
}

export default function AttachmentsSection({
    pumppuRequestId,
    attachments,
    onRefetch,
    token,
    ownerAsiakasId,
    entryByPersonId,
    disabled = false,
    onPreClaim,
    onUploadingChange,
}) {
    const { lang } = useLang();
    const t = useT();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pending, setPending] = useState([]); // [{ tempId, name }]
    const [errorMsg, setErrorMsg] = useState(null);
    const cameraInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const pdfInputRef = useRef(null);

    // Report upload-in-progress up so the wizard can block step navigation while
    // files are still uploading. Cleanup resets to false on unmount so the flag
    // can't get stuck true if the section is torn down mid-flight.
    useEffect(() => {
        onUploadingChange?.(pending.length > 0);
        return () => onUploadingChange?.(false);
    }, [pending.length, onUploadingChange]);

    const closeMenu = () => setMenuAnchor(null);

    const openCamera = () => { closeMenu(); cameraInputRef.current?.click(); };
    const openImagePicker = () => { closeMenu(); imageInputRef.current?.click(); };
    const openPdfPicker = () => { closeMenu(); pdfInputRef.current?.click(); };

    const handleAddClick = useCallback(async (e) => {
        if (disabled) return;
        // Capture anchor BEFORE any await — React synthetic events nullify
        // currentTarget after the call stack unwinds.
        const anchor = e.currentTarget;
        if (!pumppuRequestId && onPreClaim) {
            const claimed = await onPreClaim();
            if (!claimed) return;
        }
        setMenuAnchor(anchor);
    }, [disabled, pumppuRequestId, onPreClaim]);

    const uploadFiles = useCallback(async (files) => {
        if (!pumppuRequestId) return;
        setErrorMsg(null);
        const list = Array.from(files || []);
        let didUpload = false;
        for (const file of list) {
            const tempId = `pending-${Date.now()}-${Math.random()}`;
            setPending((prev) => [...prev, { tempId, name: file.name }]);
            try {
                await uploadPumppuRequestAttachment({
                    file,
                    pumppuRequestId,
                    token,
                    ownerAsiakasId,
                    entryByPersonId,
                    t,
                });
                didUpload = true;
            } catch (err) {
                captureError(err, {
                    tags: { entity: "attachment", operation: "upload" },
                    extra: { fileName: file.name },
                });
                setErrorMsg(resolveErrorMessage(err, { t, lang, fallbackKey: "attachments.uploadFailed" }));
            } finally {
                setPending((prev) => prev.filter((p) => p.tempId !== tempId));
            }
        }
        if (didUpload && onRefetch) onRefetch();
    }, [pumppuRequestId, token, ownerAsiakasId, entryByPersonId, onRefetch, lang, t]);

    const onFileChange = useCallback((e) => {
        const files = e.target.files;
        if (files?.length) uploadFiles(files);
        e.target.value = ""; // allow re-picking same file
    }, [uploadFiles]);

    const handleDelete = useCallback(async (att) => {
        const id = att.attachmentId;
        if (!id) return;
        try {
            await deleteAttachment({ attachmentId: id, token });
            onRefetch?.();
        } catch (err) {
            captureError(err, {
                tags: { entity: "attachment", operation: "delete" },
                extra: { attachmentId: id },
            });
            setErrorMsg(t("wizard.attachmentsSection.deleteFailed"));
        }
    }, [onRefetch, token, t]);

    const empty = attachments.length === 0 && pending.length === 0;

    return (
        <Box>
            <SectionHeader
                title={t("wizard.attachmentsSection.title")}
                onEdit={disabled ? undefined : handleAddClick}
                editLabel={t("wizard.attachmentsSection.addLabel")}
            />
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: 12.5, lineHeight: 1.55, mt: -0.75, mb: 1.25 }}>
                {t("wizard.attachmentsSection.helper")}
            </Typography>
            {empty ? (
                <Typography variant="body2" sx={{ fontSize: 13, color: "text.secondary" }}>
                    {t("wizard.attachmentsSection.readyHint")}
                </Typography>
            ) : (
                <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap", rowGap: 1.25 }}>
                    {pending.map((p) => (
                        <PendingThumbnail key={p.tempId} name={p.name} />
                    ))}
                    {attachments.map((a) => (
                        <Thumbnail
                            key={a.attachmentId}
                            attachment={a}
                            onDelete={handleDelete}
                            disabled={disabled}
                        />
                    ))}
                </Stack>
            )}
            {errorMsg && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                    {errorMsg}
                </Typography>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                <MenuItem onClick={openCamera}>
                    <CameraAltIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("wizard.attachmentsSection.takePhoto")}
                </MenuItem>
                <MenuItem onClick={openImagePicker}>
                    <PhotoLibraryIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("wizard.attachmentsSection.chooseImages")}
                </MenuItem>
                <MenuItem onClick={openPdfPicker}>
                    <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("wizard.attachmentsSection.addPdf")}
                </MenuItem>
            </Menu>

            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onFileChange}
                style={{ display: "none" }}
            />
            <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onFileChange}
                style={{ display: "none" }}
            />
            <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                multiple
                onChange={onFileChange}
                style={{ display: "none" }}
            />
        </Box>
    );
}
